import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { AxiosResponse } from 'axios'
import moment from 'moment'

import { Spinner } from 'core/components'
import { dateDiff, dateFormat, formatToBRL } from 'core/helpers'
import routes from 'core/routes'
import { api } from 'core/services'

import {
  Timeline,
  TimelineContainer,
  TimelineContent,
} from 'modules/console/components'

import { TRootReducer } from 'store'

import {
  Amount,
  Company,
  Content,
  CreatedAt,
  Details,
  Empty,
  ExpiredAt,
  Header,
  Sale,
  ScoreResult,
} from './styles'
import { THistoryItem, TScoreResponse } from './types'

/**
 * @function getDateTimeWithHours
 * @param date - formato: DD-MM-YYYY HH:mm:ss
 * @returns timestamp em milissegundos
 */
const getDateTimeWithHours = (date: string): number => {
  // Converte "23-12-2025 10:39:41" para timestamp
  const parts = date.match(/(\d{2})-(\d{2})-(\d{4}) (\d{2}):(\d{2}):(\d{2})/)
  if (parts) {
    const [, day, month, year, hour, minute, second] = parts
    return new Date(
      parseInt(year, 10),
      parseInt(month, 10) - 1,
      parseInt(day, 10),
      parseInt(hour, 10),
      parseInt(minute, 10),
      parseInt(second, 10),
    ).getTime()
  }
  // Fallback se o formato não bater
  return moment(dateFormat({ date, pattern: 'YYYYMMDD' })).unix() * 1000
}

function ScoreView() {
  const { app, user } = useSelector((state: TRootReducer) => state)
  const [loading, setLoading] = useState(true)
  const [history, setHistory] = useState<Array<THistoryItem>>([])

  /**
   * @function verifyDate
   * @param date
   */
  const verifyDate = (date: string): number =>
    dateDiff({
      dateStart: new Date(),
      dateEnd: date,
    })

  /**
   * @function isCurrentYear
   * @param date
   */
  const isCurrentYear = (date: string): boolean =>
    Number(date.slice(6, 10)) === new Date().getFullYear()

  useEffect(() => {
    if (user.attributes.phone_number) {
      api
        .get(
          routes.api.user.extract(user.attributes.phone_number.replace('+', ''))
            .score,
          {
            headers: {
              'X-App-Token': app.currentOrg?.token as string,
            },
          },
        )
        .then(({ data, status }: AxiosResponse<TScoreResponse>) => {
          if (status === 200 && data.client) {
            // Converter scores para itens de histórico
            const scoreItems: THistoryItem[] = (data.client.scores || []).map(
              score => {
                const diffDate = verifyDate(score.expiration_at)
                let color = 'success'

                if (score.balance === 0) {
                  color = 'gray' // Totalmente debitado
                } else if (diffDate < 0) {
                  color = 'gray' // Expirado
                } else if (diffDate <= 15) {
                  color = 'danger' // Expirando em breve
                }

                return {
                  type: 'score' as const,
                  id: score.id,
                  created_at: score.created_at,
                  company_name: score.company_name,
                  balance: score.balance,
                  score: score.score,
                  sale_amount: score.sale_amount,
                  expiration_at: score.expiration_at,
                  color,
                  diffDate,
                }
              },
            )

            // Converter resgates para itens de histórico
            const redeemingItems: THistoryItem[] = (
              data.client.redeemings || []
            ).map(redeeming => ({
              type: 'redeeming' as const,
              id: redeeming.id,
              created_at: redeeming.created_at,
              company_name: redeeming.company_name,
              debited_value: redeeming.debited_value,
              reward_name: redeeming.reward_name,
              color: 'danger', // Resgates sempre em vermelho
            }))

            /**
             * Transferência de pontos só existe em `logs_clients`: não gera linha em `scores`
             * nem em `redeemings`, então sem este bloco o cliente via o saldo mudar sem nenhum
             * registro correspondente no extrato (apontamento do QA).
             *
             * O filtro por `action` é o que evita duplicar compras e resgates, que já vêm acima
             * pelas suas próprias entidades.
             */
            const transferActions = [
              'score_transferred_out',
              'score_transferred_in',
            ]
            const transferItems: THistoryItem[] = (data.logs || [])
              .filter(log => transferActions.includes(log.action))
              .map(log => ({
                type: 'transfer' as const,
                id: log.id,
                created_at: log.created_at,
                message: log.message,
                color:
                  log.action === 'score_transferred_in' ? 'success' : 'danger',
              }))

            // Mesclar e ordenar por data (mais recente primeiro)
            const allItems = [
              ...scoreItems,
              ...redeemingItems,
              ...transferItems,
            ].sort(
              (a, b) =>
                getDateTimeWithHours(b.created_at) -
                getDateTimeWithHours(a.created_at),
            )

            setHistory(allItems)
          }
          setLoading(false)
        })
        .catch(() => {
          setHistory([])
          setLoading(false)
        })
    }
  }, [app.currentOrg?.token, user.attributes.phone_number])

  if (loading) {
    return <Spinner />
  }

  return (
    <Timeline>
      <TimelineContainer>
        {history.length ? (
          history.map((item, index) => (
            <TimelineContent
              key={`${item.type}-${item.id}`}
              dot={{ color: item.color }}
            >
              <Content>
                <Header>
                  <ScoreResult>
                    {item.type === 'score'
                      ? `${item.score} ponto${item.score! > 1 ? 's' : ''}`
                      : item.type === 'transfer'
                      ? 'Transferência de pontos'
                      : `Resgate: -${item.debited_value} ponto${
                          item.debited_value! > 1 ? 's' : ''
                        }`}
                  </ScoreResult>
                  <CreatedAt>
                    {dateFormat({
                      date: item.created_at,
                      pattern: isCurrentYear(item.created_at)
                        ? 'DD MMM'
                        : 'DD MMM YY',
                    }).toUpperCase()}
                  </CreatedAt>
                </Header>

                <Details>
                  {item.type !== 'transfer' && (
                    <Company>{item.company_name}</Company>
                  )}
                  {item.type === 'transfer' ? (
                    <Sale>
                      <Amount>
                        <span>DETALHES</span>
                        {item.message}
                      </Amount>
                    </Sale>
                  ) : item.type === 'score' ? (
                    <Sale>
                      <Amount>
                        <span>COMPRA</span>
                        {formatToBRL(item.sale_amount! / 100)}
                      </Amount>
                      <ExpiredAt>
                        <span>
                          {(item.diffDate ?? 0) < 0 ? 'EXPIROU ' : 'EXPIRA '}
                          EM
                        </span>
                        {`${dateFormat({
                          date: item.expiration_at!,
                          pattern: isCurrentYear(item.expiration_at!)
                            ? 'DD/MM HH:mm'
                            : 'DD/MM/YY HH:mm',
                        }).replace(' ', ' às ')}h`}
                      </ExpiredAt>
                    </Sale>
                  ) : (
                    <Sale>
                      <Amount>
                        <span>RECOMPENSA</span>
                        {item.reward_name}
                      </Amount>
                    </Sale>
                  )}
                </Details>
              </Content>
            </TimelineContent>
          ))
        ) : (
          <Empty>Nenhum registro encontrado.</Empty>
        )}
      </TimelineContainer>
    </Timeline>
  )
}

export { ScoreView }
